import axios from "axios";
import config from "@/config";

import type { Transaction, TransactionInput, User, UserInput, CsvImportResult, PdfImportResult, OfxImportResult, PagedResult, Budget, BudgetInput, BudgetUpdateInput, Recebivel, RecebivelInput, DashboardRecebiveis, Parcela, ContaPagar, ContaPagarInput, DashboardContasPagar, ParcelaPagar } from "@/types";
import type { Filters } from "@/components/filters/FiltersBarDashboard";

export const api = axios.create({
  baseURL: config.api.baseURL,
  timeout: config.api.timeout,
  withCredentials: true,
});

// Flag para evitar múltiplas tentativas de refresh simultâneas
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

// Callback system for AuthContext to receive new tokens after refresh
let onTokenRefreshedCallback: ((newToken: string) => void) | null = null;

export const setOnTokenRefreshed = (cb: (newToken: string) => void) => {
  onTokenRefreshedCallback = cb;
};

const clearAuthState = () => {
  localStorage.removeItem("authToken");
  localStorage.removeItem("refreshToken");
  delete (api.defaults.headers as Record<string, unknown>)["Authorization"];
};

const redirectToLogin = (reason?: string) => {
  const target = reason
    ? `/login?reason=${encodeURIComponent(reason)}`
    : "/login";

  if (window.location.pathname !== "/login") {
    window.location.href = target;
  }
};

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const handleForbidden = (error: unknown) => {
  clearAuthState();
  redirectToLogin("unauthorized");
  return Promise.reject(error);
};

const handleUnauthorized = (error: unknown) => {
  clearAuthState();
  processQueue(new Error("Unauthorized"), null);
  redirectToLogin();
  return Promise.reject(error);
};

const shouldRejectUnauthorized = (originalRequest: any, isRefreshRequest: boolean) =>
  !originalRequest || originalRequest._retry || isRefreshRequest;

const queueRequestWhileRefreshing = (originalRequest: any) =>
  new Promise((resolve, reject) => {
    failedQueue.push({ resolve, reject });
  })
    .then((token) => {
      originalRequest.headers.Authorization = `Bearer ${token}`;
      return api(originalRequest);
    })
    .catch((err) => Promise.reject(err));

const refreshAccessToken = async (originalRequest: any) => {
  try {
    const accessToken = localStorage.getItem("authToken");
    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken || !accessToken) {
      processQueue(new Error("No refresh session"), null);
      clearAuthState();
      redirectToLogin();
      return Promise.reject(new Error("No refresh session available"));
    }

    const refresh = await axios.post<{
      accessToken: string;
      refreshToken: string;
      expiresIn: number;
    }>(
      `${config.api.baseURL}/users/refresh-token`,
      { refreshToken },
      {
        timeout: config.api.timeout,
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    const newToken = refresh.data.accessToken;
    const newRefreshToken = refresh.data.refreshToken;

    api.defaults.headers.Authorization = `Bearer ${newToken}`;
    localStorage.setItem("authToken", newToken);
    if (newRefreshToken) {
      localStorage.setItem("refreshToken", newRefreshToken);
    }

    processQueue(null, newToken);

    if (onTokenRefreshedCallback) {
      onTokenRefreshedCallback(newToken);
    }

    originalRequest.headers.Authorization = `Bearer ${newToken}`;
    return api(originalRequest);
  } catch (refreshError) {
    processQueue(refreshError as Error, null);
    clearAuthState();
    redirectToLogin();
    return Promise.reject(refreshError);
  } finally {
    isRefreshing = false;
  }
};

const handleUnauthorizedRetry = (originalRequest: any) => {
  if (isRefreshing) {
    return queueRequestWhileRefreshing(originalRequest);
  }

  originalRequest._retry = true;
  isRefreshing = true;
  return refreshAccessToken(originalRequest);
};

api.interceptors.response.use(
  (response) => response,

  async (error) => {
    const originalRequest = error.config as typeof error.config & {
      _retry?: boolean;
    };
    const requestUrl = String(originalRequest?.url ?? "");
    const isRefreshRequest = requestUrl.includes("/users/refresh-token");
    const status = error.response?.status;

    if (status === 403) {
      return handleForbidden(error);
    }

    if (status === 401 && shouldRejectUnauthorized(originalRequest, isRefreshRequest)) {
      return handleUnauthorized(error);
    }

    if (status === 401 && !originalRequest._retry) {
      return handleUnauthorizedRetry(originalRequest);
    }

    if (!error.response) {
      console.error("Erro de rede:", error.message);
      return Promise.reject(new Error("Erro de conexão. Verifique sua internet."));
    }

    return Promise.reject(error);
  },
);

// Request interceptor para adicionar token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("authToken");
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

const sanitizeFilters = (filters?: Filters) => {
  if (!filters) {
    return undefined;
  }

  const entries = Object.entries(filters).filter(
    ([, value]) => value !== undefined && value !== "",
  ) as [keyof Filters, string][];

  if (entries.length === 0) {
    return undefined;
  }

  return Object.fromEntries(entries) as Filters;
};

const buildMonthRangeFilters = (): Filters => {
  const now = new Date();
  const startOfMonth = new Date(Date.UTC(now.getFullYear(), now.getMonth(), 1));
  const endOfMonth = new Date(
    Date.UTC(now.getFullYear(), now.getMonth() + 1, 0),
  );

  const formatDate = (date: Date) => date.toISOString().slice(0, 10);

  return {
    dateFrom: formatDate(startOfMonth),
    dateTo: formatDate(endOfMonth),
  };
};

export const getTransactions = async (filters?: Filters) => {
  const { pageNumber, pageSize, ...dateFilters } = filters ?? {};
  const sanitized = sanitizeFilters(dateFilters as Filters);
  const effectiveFilters = sanitized ?? buildMonthRangeFilters();
  const params = {
    ...effectiveFilters,
    ...(pageNumber !== undefined && { pageNumber }),
    ...(pageSize !== undefined && { pageSize }),
  };
  return api.get<PagedResult<Transaction>>("/transactions", { params });
};

export const createTransaction = async (data: TransactionInput) =>
  api.post<Transaction>("/transactions", data);

export const updateTransaction = async (data: TransactionInput) =>
  api.put<Transaction>("/transactions", data);

export const deleteTransaction = async (id: number | string) =>
  api.delete(`/transactions/${id}`);

export const importTransactions = async (file: File) => {
  const formData = new FormData();
  formData.append("file", file);

  return api.post<CsvImportResult>("/transactions/import", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const importPdf = async (files: File[], bankName: string, clearExisting: boolean = false) => {
  const formData = new FormData();
  files.forEach((f) => formData.append("files", f));
  formData.append("bankName", bankName);
  formData.append("clearExisting", clearExisting.toString());

  return api.post<PdfImportResult>("/transactions/import/pdf", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const importBankCsv = async (file: File, bankName: string, clearExisting: boolean = false) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("clearExisting", clearExisting.toString());

  const endpoint = bankName === "outro"
    ? "/transactions/import/csv/generic"
    : `/transactions/import/csv/${bankName}`;

  return api.post<CsvImportResult>(endpoint, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const importOfx = async (file: File, bankName: string, clearExisting: boolean = false) => {
  const formData = new FormData();
  formData.append("files", file);
  formData.append("clearExisting", clearExisting.toString());

  return api.post<OfxImportResult>(`/transactions/import/ofx/${bankName}`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

// Export transactions to CSV
const sanitizeCsvCell = (value: string): string => {
  if (/^[=+\-@\t\r]/.test(value)) {
    value = "'" + value;
  }
  return `"${value.replace(/"/g, '""')}"`;
};

export const exportTransactions = async (params?: Filters) => {
  const response = await api.get<Transaction[]>("/transactions", { params });
  const transactions = response.data;

  // CSV headers
  const headers = ["Data", "Descrição", "Valor", "Categoria", "Observações"];

  // Convert transactions to CSV rows
  const rows = transactions.map((t) => [
    new Date(t.date).toLocaleDateString("pt-BR"),
    sanitizeCsvCell(t.description),
    t.amount.toString().replace(".", ","),
    t.category,
    sanitizeCsvCell(t.notes || ""),
  ]);

  // Combine headers and rows
  const csvContent = [headers.join(";"), ...rows.map((row) => row.join(";"))].join("\n");

  // Create and download file
  const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `transacoes_${new Date().toISOString().split("T")[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const getUsers = async () => api.get<User[]>("/users");

export const getUser = async (id: number | string) =>
  api.get<User>(`/users/${id}`);

export const registerUser = async (data: UserInput) =>
  api.post<User>("/users/register", data);

export const createUser = async (data: UserInput) =>
  api.post<User>("/users", data);

export const updateUser = async (id: number | string, data: UserInput) =>
  api.put<User>(`/users/${id}`, data);

export const deleteUser = async (id: number | string) =>
  api.delete(`/users/${id}`);

export const loginUser = async (email: string, password: string) =>
  api.post("/users/login", { email, password });

export const resendActivation = async (email: string) =>
  api.post('/auth/resend-activation', { email });

export const activateUser = async (token: string) =>
  api.get('/auth/activate', { params: { token } });

export const getCurrentUser = async () =>
  api.get<User>("/users/self");

export const updateProfile = async (id: string, data: Partial<UserInput>) =>
  api.put<User>(`/users/${id}`, data);

export const updateMyProfile = async (data: { name: string; email: string; phone?: string }) =>
  api.put<User>("/users/profile", data);

export const changePassword = async (data: { currentPassword: string; newPassword: string }) =>
  api.post<{ message: string }>("/users/change-password", data);

// ── Bulk transaction endpoints ─────────────────────────────────────────────────

export interface BulkUpdateOperations {
  categoryId?: string;
  status?: string;
  paymentDate?: string;
}

export interface BulkResult {
  affectedCount: number;
  errors: string[];
  success: boolean;
}

export const bulkUpdateTransactions = async (data: { transactionIds: string[]; operations: BulkUpdateOperations }) =>
  api.post<BulkResult>("/transactions/bulk", data);

export const bulkDeleteTransactions = async (transactionIds: string[]) =>
  api.delete<BulkResult>("/transactions/bulk", { data: { transactionIds } });

// ── Budget endpoints ──────────────────────────────────────────────────────────

export const getBudgets = async () =>
  api.get<Budget[]>("/budgets");

export const getBudgetById = async (id: string) =>
  api.get<Budget>(`/budgets/${id}`);

export const createBudget = async (data: BudgetInput) =>
  api.post<Budget>("/budgets", data);

export const updateBudget = async (id: string, data: BudgetUpdateInput) =>
  api.put<Budget>(`/budgets/${id}`, data);

export const deleteBudget = async (id: string) =>
  api.delete(`/budgets/${id}`);

// ── Recebiveis endpoints ──────────────────────────────────────────────────────

export const getRecebiveis = async () =>
  api.get<Recebivel[]>("/recebiveis");

export const getRecebivelById = async (id: string) =>
  api.get<Recebivel>(`/recebiveis/${id}`);

export const createRecebivel = async (data: RecebivelInput) =>
  api.post<Recebivel>("/recebiveis", data);

export const deleteRecebivel = async (id: string) =>
  api.delete(`/recebiveis/${id}`);

export const getDashboardRecebiveis = async () =>
  api.get<DashboardRecebiveis>("/recebiveis/dashboard");

export const getParcelas = async (recebivelId: string) =>
  api.get<Parcela[]>("/parcelas", { params: { recebivelId } });

export const toggleParcelaPago = async (id: string) =>
  api.patch<Parcela>(`/parcelas/${id}/pago`);

// ── Contas a Pagar endpoints ──────────────────────────────────────────────────

export const getContasPagar = async () =>
  api.get<ContaPagar[]>("/contaspagar");

export const getContaPagarById = async (id: string) =>
  api.get<ContaPagar>(`/contaspagar/${id}`);

export const createContaPagar = async (data: ContaPagarInput) =>
  api.post<ContaPagar>("/contaspagar", data);

export const deleteContaPagar = async (id: string) =>
  api.delete(`/contaspagar/${id}`);

export const getDashboardContasPagar = async () =>
  api.get<DashboardContasPagar>("/contaspagar/dashboard");

export const getParcelasPagar = async (contaPagarId: string) =>
  api.get<ParcelaPagar[]>("/parcelaspagar", { params: { contaPagarId } });

export const toggleParcelaPagarPago = async (id: string) =>
  api.patch<ParcelaPagar>(`/parcelaspagar/${id}/pago`);
